/** Filtros y vistas del tablero asignación auxiliar ↔ clientes fiscales. */

import { isClientOperativoSuspendido } from "@/lib/clientOperativoStatus";
import { assignedUserIds, formatAssignedUsersLabel } from "@/modules/despacho/despachoAssignmentsHelpers";

export const FISCAL_BOARD_VIEW_TODAS = "todas";
export const FISCAL_BOARD_VIEW_POR_AUXILIAR = "por_auxiliar";
export const FISCAL_BOARD_VIEW_SIN_ASIGNAR = "sin_asignar";
export const FISCAL_BOARD_VIEW_SUSPENDIDAS = "suspendidas";
export const FISCAL_BOARD_VIEW_ACTIVAS = "activas";
export const FISCAL_BOARD_STAFF_FILTER_ALL = "todos";

export function isFiscalBoardStaffFilterAll(staffId) {
  return String(staffId || "").trim().toLowerCase() === FISCAL_BOARD_STAFF_FILTER_ALL;
}

export function countFiscalBoardByView(items, { scopedStaffUser } = {}) {
  const rows = Array.isArray(items) ? items : [];
  const activas = rows.filter((row) => !isClientOperativoSuspendido(row));
  const suspendidas = rows.filter((row) => isClientOperativoSuspendido(row));
  const sinAsignar = activas.filter((row) => assignedUserIds(row.assigned_users).length === 0);

  if (scopedStaffUser) {
    return {
      activas: activas.length,
      suspendidas: suspendidas.length,
    };
  }

  return {
    todas: activas.length,
    sinAsignar: sinAsignar.length,
    suspendidas: suspendidas.length,
  };
}

export function filterFiscalBoardItems(
  items,
  { view, searchQuery, filterStaffId, scopedStaffUser } = {},
) {
  const rows = Array.isArray(items) ? items : [];
  const query = String(searchQuery || "").trim().toLowerCase();
  const staffId = String(filterStaffId || "").trim();
  const viewNorm = String(view || FISCAL_BOARD_VIEW_TODAS).trim().toLowerCase();

  return rows.filter((row) => {
    if (query) {
      const rfc = String(row.rfc || "").toLowerCase();
      const name = String(row.razon_social || "").toLowerCase();
      if (!rfc.includes(query) && !name.includes(query)) return false;
    }

    const suspended = isClientOperativoSuspendido(row);

    if (scopedStaffUser) {
      if (viewNorm === FISCAL_BOARD_VIEW_SUSPENDIDAS) return suspended;
      return !suspended;
    }

    if (viewNorm === FISCAL_BOARD_VIEW_SUSPENDIDAS) return suspended;
    if (suspended) return false;

    if (viewNorm === FISCAL_BOARD_VIEW_SIN_ASIGNAR) {
      return assignedUserIds(row.assigned_users).length === 0;
    }

    if (
      (viewNorm === FISCAL_BOARD_VIEW_POR_AUXILIAR || viewNorm === FISCAL_BOARD_VIEW_TODAS) &&
      staffId &&
      !isFiscalBoardStaffFilterAll(staffId)
    ) {
      return assignedUserIds(row.assigned_users).includes(staffId);
    }

    return true;
  });
}

/**
 * Resumen por auxiliar (vista «Todos»): incluye auxiliares activos aunque tengan 0 empresas.
 * Una empresa con varios auxiliares aparece en cada sección correspondiente.
 */
export function buildFiscalBoardStaffSummary(items, staffOptions, { searchQuery } = {}) {
  const activeRows = filterFiscalBoardItems(items, {
    view: FISCAL_BOARD_VIEW_TODAS,
    searchQuery,
  });

  const sectionById = new Map();
  for (const staff of staffOptions || []) {
    if (!staff?.id) continue;
    sectionById.set(staff.id, {
      staffId: staff.id,
      staffLabel: staff.name || staff.email || staff.id,
      staffRole: staff.despacho_role || "",
      rows: [],
    });
  }

  for (const row of activeRows) {
    const ids = assignedUserIds(row.assigned_users);
    for (const uid of ids) {
      if (!sectionById.has(uid)) {
        const match = (row.assigned_users || []).find((u) => u.user_id === uid);
        sectionById.set(uid, {
          staffId: uid,
          staffLabel: match?.name || uid,
          staffRole: match?.despacho_role || "",
          rows: [],
        });
      }
      const section = sectionById.get(uid);
      if (!section.rows.some((r) => r.fiscal_client_id === row.fiscal_client_id)) {
        section.rows.push(row);
      }
    }
  }

  const sections = [...sectionById.values()].sort((a, b) =>
    a.staffLabel.localeCompare(b.staffLabel, "es"),
  );

  const unassignedRows = activeRows.filter((row) => assignedUserIds(row.assigned_users).length === 0);

  return { sections, unassignedRows };
}

export function isFiscalBoardStaffSummaryView(view, filterStaffId) {
  return (
    String(view || "").trim().toLowerCase() === FISCAL_BOARD_VIEW_POR_AUXILIAR &&
    isFiscalBoardStaffFilterAll(filterStaffId)
  );
}

export function fiscalBoardRowsEligibleForBulk(rows) {
  return (rows || []).filter(
    (row) => row?.fiscal_client_id && !isClientOperativoSuspendido(row),
  );
}

/** Auxiliar efectivo para bulk: requiere auxiliar concreto (no «todos»). */
export function resolveFiscalBoardBulkStaffId({ boardView, filterStaffId, bulkStaffId } = {}) {
  const bulk = String(bulkStaffId || "").trim();
  const filter = String(filterStaffId || "").trim();
  const view = String(boardView || "").trim().toLowerCase();

  if (view === FISCAL_BOARD_VIEW_POR_AUXILIAR) {
    if (!isFiscalBoardStaffFilterAll(filter)) {
      return filter;
    }
    return isFiscalBoardStaffFilterAll(bulk) ? "" : bulk;
  }

  return isFiscalBoardStaffFilterAll(bulk) ? "" : bulk;
}

/** Parámetro staff_user_id para export Excel/PDF alineado con filtros visibles en UI. */
export function resolveFiscalBoardExportStaffId({ boardView, filterStaffId, bulkStaffId } = {}) {
  const view = String(boardView || "").trim().toLowerCase();
  if (view === FISCAL_BOARD_VIEW_SUSPENDIDAS || view === FISCAL_BOARD_VIEW_SIN_ASIGNAR) {
    return "";
  }
  if (view === FISCAL_BOARD_VIEW_POR_AUXILIAR) {
    return String(filterStaffId || FISCAL_BOARD_STAFF_FILTER_ALL).trim();
  }
  if (view === FISCAL_BOARD_VIEW_TODAS) {
    return String(bulkStaffId || FISCAL_BOARD_STAFF_FILTER_ALL).trim();
  }
  return "";
}

export function filterRowsAssignedToStaff(rows, staffId) {
  const id = String(staffId || "").trim();
  if (!id) return [];
  return (rows || []).filter((row) => assignedUserIds(row?.assigned_users).includes(id));
}

/**
 * Aplica en memoria assigned_users devueltos por bulk/PUT sin recargar toda la tabla.
 * @param {object[]} items
 * @param {Array<{ fiscal_client_id?: string, assigned_users?: object[] }>} updates
 */
export function mergeFiscalBoardAssignmentUpdates(items, updates) {
  const patches = Array.isArray(updates) ? updates : [];
  if (!patches.length) return items || [];

  const byFiscalId = new Map();
  for (const patch of patches) {
    const fid = String(patch?.fiscal_client_id || "").trim();
    if (!fid) continue;
    byFiscalId.set(fid, Array.isArray(patch.assigned_users) ? patch.assigned_users : []);
  }
  if (byFiscalId.size === 0) return items || [];

  return (items || []).map((row) => {
    const fid = row?.fiscal_client_id;
    if (!fid || !byFiscalId.has(fid)) return row;
    return { ...row, assigned_users: byFiscalId.get(fid) };
  });
}

const EMPTY_AUX_TASK_STATS = {
  total: 0,
  pending: 0,
  in_progress: 0,
  overdue: 0,
  done: 0,
  not_applicable: 0,
};

const TASK_CLOSED_STATUSES = new Set([
  "done",
  "completada",
  "cancelled",
  "cancelada",
  "not_applicable_this_period",
  "no_aplico_este_mes",
]);

function _normalizeTaskStatusForSummary(status) {
  const st = String(status || "pending").toLowerCase();
  if (st === "pendiente" || st === "requested" || st === "solicitado") return "pending";
  if (st === "en_proceso") return "in_progress";
  if (st === "completada") return "done";
  if (st === "no_aplico_este_mes") return "not_applicable_this_period";
  return st;
}

function _taskCountsByAuxiliar(tasks, todayIso) {
  const byUser = new Map();
  for (const row of tasks || []) {
    const uid = row?.assigned_to_user_id;
    if (!uid) continue;
    const bucket = byUser.get(uid) || { ...EMPTY_AUX_TASK_STATS };
    const st = _normalizeTaskStatusForSummary(row.status);
    bucket.total += 1;
    if (st === "pending") bucket.pending += 1;
    else if (st === "in_progress") bucket.in_progress += 1;
    else if (st === "done") bucket.done += 1;
    else if (st === "not_applicable_this_period") bucket.not_applicable += 1;
    const due = String(row.due_date || row.fecha_fiscal || "").slice(0, 10);
    if (due && todayIso && due < todayIso && !TASK_CLOSED_STATUSES.has(st)) {
      bucket.overdue += 1;
    }
    byUser.set(uid, bucket);
  }
  return byUser;
}

function _companiesByAuxiliarFromBoard(boardRows) {
  const byUser = new Map();
  for (const row of boardRows || []) {
    const fid = row?.fiscal_client_id;
    if (!fid) continue;
    const label = String(row.razon_social || row.rfc || fid).trim();
    for (const au of row.assigned_users || []) {
      const uid = au?.user_id;
      if (!uid) continue;
      const entry = byUser.get(uid) || {
        auxiliar_name: au.name || uid,
        assigned_companies: [],
      };
      if (!entry.assigned_companies.some((c) => c.id === fid)) {
        entry.assigned_companies.push({
          id: fid,
          name: label,
          rfc: row.rfc || "",
          suspended: isClientOperativoSuspendido(row),
        });
      }
      if (au.name) entry.auxiliar_name = au.name;
      byUser.set(uid, entry);
    }
  }
  return byUser;
}

/**
 * Resumen por auxiliar: empresas del tablero de asignación + conteos de tareas del periodo.
 */
export function buildFiscalAuxiliarSummary({
  boardRows = [],
  reportRows = [],
  tasks = [],
  todayIso = new Date().toISOString().slice(0, 10),
} = {}) {
  const companiesByUser = _companiesByAuxiliarFromBoard(boardRows);
  const taskCountsByUser = _taskCountsByAuxiliar(tasks, todayIso);
  const byUser = new Map();

  for (const row of reportRows || []) {
    const uid = row?.assigned_to_user_id;
    if (!uid) continue;
    byUser.set(uid, {
      assigned_to_user_id: uid,
      auxiliar_name: row.auxiliar_name || uid,
      assigned_companies: [],
      clients_assigned: 0,
      assigned_companies_label: "—",
      ...EMPTY_AUX_TASK_STATS,
    });
  }

  for (const [uid, info] of companiesByUser.entries()) {
    const base = byUser.get(uid) || {
      assigned_to_user_id: uid,
      auxiliar_name: info.auxiliar_name || uid,
      ...EMPTY_AUX_TASK_STATS,
    };
    byUser.set(uid, {
      ...base,
      auxiliar_name: info.auxiliar_name || base.auxiliar_name,
      assigned_companies: info.assigned_companies,
      clients_assigned: info.assigned_companies.length,
      assigned_companies_label: info.assigned_companies.map((c) => c.name).join(", ") || "—",
    });
  }

  for (const [uid, stats] of taskCountsByUser.entries()) {
    const base = byUser.get(uid) || {
      assigned_to_user_id: uid,
      auxiliar_name: uid,
      assigned_companies: [],
      clients_assigned: 0,
      assigned_companies_label: "—",
      ...EMPTY_AUX_TASK_STATS,
    };
    byUser.set(uid, { ...base, ...stats });
  }

  return [...byUser.values()]
    .filter((row) => row.assigned_to_user_id)
    .sort((a, b) => String(a.auxiliar_name || "").localeCompare(String(b.auxiliar_name || ""), "es"));
}

/**
 * Secciones por empresa: catálogo fiscal + tareas del periodo (aunque no haya tareas).
 */
export function buildFiscalCompanySections(boardRows = [], tasks = []) {
  const tasksByClient = new Map();
  for (const task of tasks || []) {
    const cid = task?.fiscal_client_id;
    if (!cid) continue;
    if (!tasksByClient.has(cid)) tasksByClient.set(cid, []);
    tasksByClient.get(cid).push(task);
  }

  const seen = new Set();
  const sections = [];

  for (const row of boardRows || []) {
    const cid = row?.fiscal_client_id;
    if (!cid || seen.has(cid)) continue;
    seen.add(cid);
    const name = String(row.razon_social || row.rfc || cid).trim();
    sections.push({
      id: cid,
      clientName: name,
      rfc: row.rfc || "",
      assignedLabel: formatAssignedUsersLabel(row.assigned_users),
      assigned_users: row.assigned_users || [],
      suspended: isClientOperativoSuspendido(row),
      tasks: tasksByClient.get(cid) || [],
    });
  }

  for (const [cid, taskRows] of tasksByClient.entries()) {
    if (seen.has(cid)) continue;
    seen.add(cid);
    sections.push({
      id: cid,
      clientName: taskRows[0]?.client_name || cid,
      rfc: taskRows[0]?.client_rfc || "",
      assignedLabel: taskRows[0]?.assigned_to_name || "Sin asignar",
      assigned_users: [],
      suspended: false,
      tasks: taskRows,
    });
  }

  return sections.sort((a, b) => String(a.clientName || "").localeCompare(String(b.clientName || ""), "es"));
}

/** Mapa fiscal_client_id → auxiliar primary (primer asignado). */
export function buildFiscalClientAuxMap(boardRows = []) {
  const map = {};
  for (const row of boardRows || []) {
    const fid = row?.fiscal_client_id;
    if (!fid) continue;
    const ids = assignedUserIds(row.assigned_users);
    if (ids.length && !map[fid]) map[fid] = ids[0];
  }
  return map;
}

/**
 * Secciones «Pendientes del mes» por auxiliar: empresas, tareas e instancias del periodo.
 * Solo incluye auxiliares con cartera, tareas o vencimientos.
 */
export function buildFiscalPendingSections({
  auxSummary = [],
  tasks = [],
  complianceInstances = [],
  clientToAuxMap = {},
} = {}) {
  const tasksByAux = new Map();
  for (const task of tasks || []) {
    const uid = task?.assigned_to_user_id || "__sin_asignar__";
    if (!tasksByAux.has(uid)) tasksByAux.set(uid, []);
    tasksByAux.get(uid).push(task);
  }

  const instancesByAux = new Map();
  for (const inst of complianceInstances || []) {
    const fid = inst?.fiscal_client_id;
    const uid = inst?.assigned_auxiliar_id || (fid && clientToAuxMap[fid]) || "__sin_asignar__";
    if (!instancesByAux.has(uid)) instancesByAux.set(uid, []);
    instancesByAux.get(uid).push(inst);
  }

  const sections = (auxSummary || []).map((aux) => ({
    ...aux,
    tasks: tasksByAux.get(aux.assigned_to_user_id) || [],
    instances: instancesByAux.get(aux.assigned_to_user_id) || [],
  }));

  const knownIds = new Set(sections.map((s) => s.assigned_to_user_id));
  for (const [uid, rows] of tasksByAux.entries()) {
    if (uid === "__sin_asignar__" || knownIds.has(uid)) continue;
    sections.push({
      assigned_to_user_id: uid,
      auxiliar_name: rows[0]?.assigned_to_name || uid,
      clients_assigned: 0,
      assigned_companies: [],
      assigned_companies_label: "—",
      tasks: rows,
      instances: instancesByAux.get(uid) || [],
    });
    knownIds.add(uid);
  }

  const unassignedTasks = tasksByAux.get("__sin_asignar__") || [];
  const unassignedInstances = instancesByAux.get("__sin_asignar__") || [];
  if (unassignedTasks.length || unassignedInstances.length) {
    sections.push({
      assigned_to_user_id: "__sin_asignar__",
      auxiliar_name: "Sin asignar",
      clients_assigned: 0,
      assigned_companies: [],
      assigned_companies_label: "—",
      tasks: unassignedTasks,
      instances: unassignedInstances,
    });
  }

  return sections
    .filter(
      (s) =>
        (s.assigned_companies || []).length > 0
        || (s.clients_assigned || 0) > 0
        || (s.tasks || []).length > 0
        || (s.instances || []).length > 0,
    )
    .sort((a, b) => String(a.auxiliar_name || "").localeCompare(String(b.auxiliar_name || ""), "es"));
}

/**
 * Agrupa tareas e instancias de una sección auxiliar por empresa asignada.
 * Incluye empresas de la cartera aunque no tengan vencimientos en el periodo.
 */
export function groupFiscalPendingByCompany(section = {}) {
  const companies = new Map();

  const ensureCompany = (id, meta = {}) => {
    const key = String(id || "").trim();
    if (!key) return null;
    if (!companies.has(key)) {
      companies.set(key, {
        id: key,
        name: meta.name || key,
        rfc: meta.rfc || "",
        suspended: Boolean(meta.suspended),
        tasks: [],
        instances: [],
      });
    }
    return companies.get(key);
  };

  for (const company of section.assigned_companies || []) {
    ensureCompany(company.id, {
      name: company.name,
      rfc: company.rfc,
      suspended: company.suspended,
    });
  }

  for (const task of section.tasks || []) {
    const row = ensureCompany(task.fiscal_client_id, {
      name: task.client_name,
      rfc: task.client_rfc,
    });
    if (row) row.tasks.push(task);
  }

  for (const inst of section.instances || []) {
    const row = ensureCompany(inst.fiscal_client_id, {
      name: inst.client_name,
      rfc: inst.client_rfc,
    });
    if (row) row.instances.push(inst);
  }

  return Array.from(companies.values()).sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "es"),
  );
}
